import OpenAI from 'openai';

/**
 * CRITICAL: Initialize OpenAI client with enhanced stability
 */
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true, // Required for client-side usage in React
});

/**
 * CRITICAL: Enhanced model mapping with stability focus
 */
export const getOpenAIModel = (modelName) => {
  const modelMap = {
    // Production stable models - CRITICAL: Use most stable versions
    'gpt-4': 'gpt-4',
    'gpt-4-turbo': 'gpt-4-turbo-preview', 
    'gpt-3.5-turbo': 'gpt-3.5-turbo',
    'gpt-4o': 'gpt-4o',
    'gpt-4o-mini': 'gpt-4o-mini',
    
    // Fallback mappings for compatibility - CRITICAL: Map to stable alternatives
    'gpt-5': 'gpt-4-turbo', // CRITICAL: Use stable model instead
    'gpt-5-mini': 'gpt-4o-mini',
    'gpt-5-nano': 'gpt-3.5-turbo',
    'claude-3-opus': 'gpt-4-turbo',
    'claude-3-sonnet': 'gpt-4o-mini',
    'gemini-pro': 'gpt-4o-mini'
  };
  
  return modelMap?.[modelName] || 'gpt-4-turbo';
};

/**
 * Checks if an error is related to organization verification
 * @param {Error} error - The error object
 * @returns {boolean} True if it's an organization verification error
 */
const isOrganizationError = (error) => {
  return error?.message?.includes('organization must be verified') || 
         error?.message?.includes('organization/general') ||
         error?.status === 400 && error?.message?.includes('verified');
};

/**
 * CRITICAL: Enhanced chat completion with response immutability
 */
export async function getChatCompletion(userMessage, config = {}) {
  const startTime = Date.now();
  const requestId = `req-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
  
  console.log(`[OpenAI Service] Starting completion request ${requestId}`);
  
  try {
    const model = getOpenAIModel(config?.model || 'gpt-4-turbo');
    const systemMessage = config?.systemPrompt || 'You are a helpful AI assistant.';
    
    // CRITICAL: Use deterministic parameters for stable responses
    const requestConfig = {
      model,
      messages: [
        { role: 'system', content: systemMessage },
        { role: 'user', content: userMessage },
      ],
      temperature: config?.temperature ?? 0.7, // CRITICAL: Fixed temperature for consistency
      max_tokens: config?.maxTokens || 2048,
      top_p: config?.topP ?? 1.0,
      frequency_penalty: config?.frequencyPenalty ?? 0.0,
      presence_penalty: config?.presencePenalty ?? 0.0,
      // CRITICAL: Add seed for deterministic responses in compatible models
      ...(model?.includes('gpt-4') && { seed: Math.floor(Date.now() / 1000) })
    };

    console.log(`[OpenAI Service] Sending request ${requestId} with model ${model}`);
    const response = await openai?.chat?.completions?.create(requestConfig);

    const endTime = Date.now();
    const responseTime = endTime - startTime;
    
    // CRITICAL: Create immutable response structure
    const stableResponse = Object.freeze({
      content: response?.choices?.[0]?.message?.content,
      stable: true, // CRITICAL: Mark as stable
      immutable: true,
      requestId,
      metadata: Object.freeze({
        responseTime,
        tokens: response?.usage?.total_tokens || Math.floor(response?.choices?.[0]?.message?.content?.length / 4),
        model: model,
        finishReason: response?.choices?.[0]?.finish_reason,
        promptTokens: response?.usage?.prompt_tokens || 0,
        completionTokens: response?.usage?.completion_tokens || 0,
        timestamp: Date.now(),
        stable: true,
        locked: true
      })
    });
    
    console.log(`[OpenAI Service] Completion ${requestId} successful: ${stableResponse?.content?.length} chars`);
    return stableResponse;
  } catch (error) {
    console.error(`[OpenAI Service] Error in completion ${requestId}:`, error);
    const endTime = Date.now();
    
    // Handle organization verification errors specifically
    if (isOrganizationError(error)) {
      return Object.freeze({
        content: 'I apologize, but there\'s a temporary issue with the AI service configuration. Please try again in a moment.',
        error: true,
        errorMessage: 'Organization verification required',
        errorType: 'organization_verification',
        requestId,
        stable: true,
        metadata: Object.freeze({
          responseTime: endTime - startTime,
          tokens: 0,
          model: config?.model || 'gpt-4-turbo',
          error: 'Organization verification required',
          timestamp: Date.now(),
          stable: true
        })
      });
    }
    
    return Object.freeze({
      content: 'Sorry, I encountered an error while processing your request. Please try again.',
      error: true,
      errorMessage: error?.message,
      requestId,
      stable: true,
      metadata: Object.freeze({
        responseTime: endTime - startTime,
        tokens: 0,
        model: config?.model || 'gpt-4-turbo',
        error: error?.message,
        timestamp: Date.now(),
        stable: true
      })
    });
  }
}

/**
 * CRITICAL: Enhanced streaming with absolute race condition prevention
 */
export async function getStreamingChatCompletion(userMessage, onChunk, config = {}) {
  const startTime = Date.now();
  const streamId = `stream-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
  let fullResponse = '';
  let streamCompleted = false; // CRITICAL: Stream completion flag
  let chunkCount = 0;
  
  console.log(`[OpenAI Service] Starting streaming request ${streamId}`);
  
  try {
    const model = getOpenAIModel(config?.model || 'gpt-4-turbo');
    const systemMessage = config?.systemPrompt || 'You are a helpful AI assistant.';
    
    const requestConfig = {
      model,
      messages: [
        { role: 'system', content: systemMessage },
        { role: 'user', content: userMessage },
      ],
      stream: true,
      temperature: config?.temperature ?? 0.7, // CRITICAL: Fixed temperature
      max_tokens: config?.maxTokens || 2048,
      // CRITICAL: Add seed for deterministic streaming in compatible models
      ...(model?.includes('gpt-4') && { seed: Math.floor(Date.now() / 1000) })
    };

    const stream = await openai?.chat?.completions?.create(requestConfig);

    for await (const chunk of stream) {
      // CRITICAL: Only process if stream hasn't been marked complete
      if (streamCompleted) {
        console.log(`[OpenAI Service] Stream ${streamId} already completed, ignoring chunk`);
        break;
      }
      
      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        fullResponse += content;
        chunkCount++;
        console.log(`[OpenAI Service] Stream ${streamId} chunk ${chunkCount}: +${content?.length} chars (total: ${fullResponse?.length})`);
        onChunk(content);
      }
      
      // CRITICAL: Check for stream completion
      if (chunk?.choices?.[0]?.finish_reason) {
        console.log(`[OpenAI Service] Stream ${streamId} finished with reason: ${chunk?.choices?.[0]?.finish_reason}`);
        streamCompleted = true;
        break;
      }
    }

    const endTime = Date.now();
    
    // CRITICAL: Mark stream as complete and create immutable response
    streamCompleted = true;
    const stableResponse = Object.freeze({
      content: fullResponse,
      stable: true, // CRITICAL: Mark as stable
      immutable: true,
      streamId,
      metadata: Object.freeze({
        responseTime: endTime - startTime,
        tokens: Math.floor(fullResponse?.length / 4),
        model: model,
        streaming: true,
        chunkCount,
        timestamp: Date.now(),
        stable: true,
        locked: true,
        finalLength: fullResponse?.length
      })
    });
    
    console.log(`[OpenAI Service] Streaming ${streamId} completed: ${fullResponse?.length} chars in ${chunkCount} chunks`);
    return stableResponse;
  } catch (error) {
    console.error(`[OpenAI Service] Error in streaming ${streamId}:`, error);
    streamCompleted = true; // CRITICAL: Stop stream processing
    const endTime = Date.now();
    
    // Handle organization verification error with fallback
    if (isOrganizationError(error)) {
      try {
        console.warn(`[OpenAI Service] Streaming ${streamId} failed, attempting fallback...`);
        
        // CRITICAL: Fallback to regular completion
        const fallbackResponse = await getChatCompletion(userMessage, config);
        
        if (!fallbackResponse?.error && fallbackResponse?.content) {
          // Simulate streaming by sending content in chunks
          const content = fallbackResponse?.content;
          const words = content?.split(' ') || [];
          
          console.log(`[OpenAI Service] Simulating streaming for ${streamId} with ${words?.length} words`);
          
          // Send words with small delay to simulate streaming
          for (let i = 0; i < words?.length && !streamCompleted; i++) {
            const word = words?.[i] + (i < words?.length - 1 ? ' ' : '');
            onChunk(word);
            // Small delay only in development
            if (import.meta.env?.DEV && i < words?.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 30));
            }
          }
          
          return Object.freeze({
            content: content,
            stable: true, // CRITICAL: Mark as stable
            immutable: true,
            streamId,
            metadata: Object.freeze({
              responseTime: endTime - startTime,
              tokens: fallbackResponse?.metadata?.tokens,
              model: fallbackResponse?.metadata?.model,
              streaming: false,
              fallback: true,
              originalError: 'Streaming requires organization verification',
              timestamp: Date.now(),
              stable: true
            })
          });
        }
      } catch (fallbackError) {
        console.error(`[OpenAI Service] Fallback also failed for ${streamId}:`, fallbackError);
      }
    }
    
    const errorMessage = isOrganizationError(error) 
      ? 'I apologize, but streaming is temporarily unavailable. Please try again.' :'Sorry, I encountered an error while streaming the response.';
    
    onChunk(errorMessage);
    
    return Object.freeze({
      content: errorMessage,
      error: true,
      errorMessage: error?.message,
      errorType: isOrganizationError(error) ? 'organization_verification' : 'general',
      streamId,
      stable: true,
      metadata: Object.freeze({
        responseTime: endTime - startTime,
        tokens: 0,
        model: config?.model || 'gpt-4-turbo',
        streaming: true,
        error: error?.message,
        timestamp: Date.now(),
        stable: true
      })
    });
  }
}

/**
 * Generates a structured response using compatible models.
 * @param {string} userMessage - The user's input message.
 * @param {Object} schema - JSON schema for structured response
 * @param {Object} config - Configuration options
 * @returns {Promise<Object>} Structured response object.
 */
export async function getStructuredResponse(userMessage, schema, config = {}) {
  const startTime = Date.now();
  
  try {
    // Use a more compatible model for structured responses
    const model = 'gpt-3.5-turbo';
    
    // For structured responses, we'll use a prompt-based approach instead of response_format
    // since it's more widely supported
    const structuredPrompt = `${config?.systemPrompt || 'You are a helpful assistant that provides structured responses.'}

Please respond to the following request with a valid JSON object that follows this schema:
${JSON.stringify(schema, null, 2)}

User request: ${userMessage}

Important: Respond ONLY with valid JSON, no additional text or explanation.`;

    const response = await openai?.chat?.completions?.create({
      model,
      messages: [
        { role: 'user', content: structuredPrompt }
      ],
      temperature: config?.temperature || 0.3, // Lower temperature for more consistent structured output
      max_tokens: config?.maxTokens || 2048,
    });

    const endTime = Date.now();
    const content = response?.choices?.[0]?.message?.content;
    
    try {
      const parsedResponse = JSON.parse(content);
      
      return {
        data: parsedResponse,
        metadata: {
          responseTime: endTime - startTime,
          tokens: response?.usage?.total_tokens || 0,
          model: model,
          structured: true
        }
      };
    } catch (parseError) {
      console.error('Failed to parse structured response:', parseError);
      
      return {
        data: { 
          error: 'Failed to generate valid structured response', 
          rawResponse: content,
          message: 'The AI response was not in the expected JSON format'
        },
        error: true,
        metadata: {
          responseTime: endTime - startTime,
          tokens: response?.usage?.total_tokens || 0,
          model: model,
          structured: true,
          parseError: parseError?.message
        }
      };
    }
  } catch (error) {
    console.error('Error in structured response:', error);
    const endTime = Date.now();
    
    return {
      data: { 
        error: 'Failed to generate structured response', 
        message: error?.message 
      },
      error: true,
      metadata: {
        responseTime: endTime - startTime,
        tokens: 0,
        model: config?.model || 'gpt-3.5-turbo',
        structured: true,
        error: error?.message
      }
    };
  }
}

/**
 * Generates enhanced prompts for better AI responses.
 * @param {string} originalPrompt - The original prompt
 * @param {string} context - Additional context
 * @returns {Promise<string>} Enhanced prompt
 */
export async function enhancePrompt(originalPrompt, context = '') {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert at creating detailed, effective prompts for AI assistants. Enhance the user\'s prompt with better context, clarity, and structure while maintaining their core intent.' 
        },
        { 
          role: 'user', 
          content: `Context: ${context}\n\nOriginal prompt: "${originalPrompt}"\n\nPlease enhance this prompt for better AI responses.` 
        },
      ],
      temperature: 0.7,
      max_tokens: 1000,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Error enhancing prompt:', error);
    return originalPrompt; // Fallback to original
  }
}

/**
 * Validates OpenAI connection and API key.
 * @returns {Promise<Object>} Connection status and info
 */
export async function validateConnection() {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo',
      messages: [{ role: 'user', content: 'Test connection' }],
      max_tokens: 10,
      temperature: 0.1
    });
    
    return {
      connected: true,
      model: 'gpt-3.5-turbo',
      usage: response?.usage || { total_tokens: 0 }
    };
  } catch (error) {
    const isOrgError = isOrganizationError(error);
    
    return {
      connected: false,
      error: error?.message,
      errorType: isOrgError ? 'organization_verification' : 'general',
      suggestion: isOrgError ? 
        'Organization verification required. Some features may be limited.' : 
        error?.message?.includes('API key') ? 
          'Please check your VITE_OPENAI_API_KEY in .env file': 'Please check your internet connection'
    };
  }
}

export default openai;